import styled from '@emotion/styled';
import { Trans, useLingui } from '@lingui/react/macro';
import { InfoOutlined } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { useAtom } from 'jotai';

import { usePreferences } from '@/react-query/usePreferences';

import { VideaTypography } from '../../../../common-components/elements/VideaTypography/VideaTypography';
import {
  black,
  blueGrey,
  textPrimary,
} from '../../../../theme/videa-style-tokens';
import { mixpanelEvents, useTrackEvent } from '../../../../utils/mixpanel';
import { ConfidenceDialog } from '../../displayThreshold/ConfidenceDialog';
import { ToolbarButton } from '../../imageToolbar/ToolbarButton';
import type { ImageObject } from '../../types';

import { binFilterAtom } from './atoms';
import {
  useDoAnalysesHaveConfidenceScore,
  useImageFindingCounts,
} from './helpers';

const Confidence = styled.div`
  align-items: center;
  justify-content: start;
  width: 100%;
  padding: 0 0 0 0;
  .sidebar-count {
    color: ${textPrimary};
    margin-top: 0;
    display: flex;
    align-items: center;
    width: 130px;
    gap: 0.25em;
    font-size: 0.65em;
    font-weight: 500;
    padding: 0 0.85em;
  }
  .sidebar-title {
    font-size: 0.875em;
    line-height: 1em;
    font-weight: 600;
  }

  .sidebar-subtitle {
    display: flex;
    align-items: center;
    gap: 0.5em;
    flex-wrap: nowrap;

    .MuiSvgIcon-root {
      height: 1em;
    }
  }

  .confidence-settings-text {
    color: #fff;
    display: flex;
    align-items: center;
    width: 115px;
    gap: 0.25em;
    height: 3em;
    font-size: 0.75em;
    font-weight: 500;
    padding: 0.5em 0.75em 0.5em 0.75em;
  }

  .confidence-controls {
    position: relative;
    display: flex;
    width: 100%;
    height: auto;
    flex-direction: row;
    align-items: center;
    justify-content: start;
    gap: 0.25em;
  }

  .confidence-slider-group {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 4px;
    padding: 0 0.25em;
  }

  .confidence-slider-group {
    color: ${black};
  }
`;

export const ConfidenceScoreFilterV2 = ({
  image,
}: {
  image: ImageObject | null;
}) => {
  const [binFilter, setBinFilter] = useAtom(binFilterAtom);
  const { trackEvent } = useTrackEvent();
  const { t } = useLingui();

  const { modelsWithScores } = useDoAnalysesHaveConfidenceScore();
  const { allFindingsTotal, allFindingsHiddenTotal, findingsDescription } =
    useImageFindingCounts(image);
  const { data: prefs } = usePreferences();

  const imageCounts = (
    <div className="sidebar-count">
      <VideaTypography variant="labelSmall" className="sidebar-title">
        <Trans>{allFindingsTotal} Findings</Trans>
      </VideaTypography>
      <VideaTypography variant="bodyExtrasmall" className="sidebar-subtitle">
        <Trans>
          {allFindingsHiddenTotal} hidden){' '}
          <Tooltip title={findingsDescription}>
            <InfoOutlined style={{ fontSize: '12px' }} />
          </Tooltip>
        </Trans>
      </VideaTypography>
    </div>
  );

  const handleButtonClick = (value: 0 | 1 | 2 | 3) => {
    setBinFilter(value);
    trackEvent(mixpanelEvents.visitBinThresholdChanged, {
      value,
    });
  };

  return (
    <Confidence className="confidence-controls-group" data-testid="main">
      <div className="confidence-controls" data-testid="confidence-controls">
        <div className="confidence-settings-text">
          <Trans>
            Display Threshold
            <Tooltip
              title={
                modelsWithScores.length
                  ? t`The following findings can be adjusted using the buttons: ` +
                    modelsWithScores.join(', ') +
                    t`. Use the buttons to temporarily show more or fewer restorative findings from Videa Dental AI for this visit only. To change your practice's default display setting (for all images), click "Threshold Preference" to the right of the buttons.`
                  : t`Display Threshold not available for this image set.`
              }
            >
              <InfoOutlined style={{ fontSize: '16px' }} />
            </Tooltip>
          </Trans>
        </div>
        <div
          className="confidence-slider-group"
          data-testid="confidence-slider-group"
        >
          <ToolbarButton
            dataTestId="less-toggle"
            isActive={binFilter === 0}
            onClick={() => handleButtonClick(0)}
            variant="default"
          >
            <Trans>Less</Trans>
          </ToolbarButton>
          <ToolbarButton
            dataTestId="balanced-toggle"
            isActive={binFilter === 1}
            onClick={() => handleButtonClick(1)}
            variant="default"
          >
            <Trans>Balanced</Trans>
          </ToolbarButton>
          <ToolbarButton
            dataTestId="more-toggle"
            isActive={binFilter === 2}
            onClick={() => handleButtonClick(2)}
            variant="default"
          >
            <Trans>More</Trans>
          </ToolbarButton>
          <ToolbarButton
            dataTestId="display-all-toggle"
            isActive={binFilter === 3}
            onClick={() => handleButtonClick(3)}
            variant="default"
          >
            <Trans>All</Trans>
          </ToolbarButton>
          {!prefs?.disablePracticePreferences && <ConfidenceDialog />}
        </div>
      </div>
      {image && imageCounts}
    </Confidence>
  );
};
